import { format } from 'date-fns';
import * as React from 'react';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import { Table } from '../../../../components/tables/Table';
import { DealerDivisionOrdering, DealerSales, DealerSalesYear } from '../../../../support/models/dealers/dealer-sales';
import { formatCurrency } from '../../../../utils/currency-formatter';
import { DealerSalesChart, DIVISION_COLORS, DIVISION_NAMES, MONTHS } from './DealerSalesChart';

interface IProps {
    sales: DealerSales | undefined;
}

const lastOrder = (ordering: DealerDivisionOrdering) =>
    ordering.lastOrderDate ? format(ordering.lastOrderDate, 'MMM D, YYYY') : 'No orders';

const averageDays = (ordering: DealerDivisionOrdering) => {
    const days = ordering.averageDaysBetweenOrders;
    if (days === null || days === undefined) {
        return 'Not enough orders for an average';
    }

    return `Avg ${days.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${days === 1 ? 'day' : 'days'} between orders`;
};

const OrderingTile = (props: { title: string, ordering: DealerDivisionOrdering }) => (
    <div className="dealer-sales-tile">
        <div className="dealer-sales-tile-label">{props.title}</div>
        <div className="dealer-sales-tile-value">{lastOrder(props.ordering)}</div>
        <div className="dealer-sales-tile-note">Last order</div>
        {props.ordering.lastOrderDate ? <div className="dealer-sales-tile-note">{averageDays(props.ordering)}</div> : null}
    </div>
);

/**
 * The sales of a dealer: what it has bought this year and in the last twelve months, a chart of a year by month and
 * division, and the figures of each month. They are the subtotals of its invoices, so freight and tax are not in them.
 */
export const DealerSalesView = (props: IProps) => {

    const { sales } = props;
    const [chosenYear, setChosenYear] = React.useState<number | undefined>(undefined);

    const money = (amount: number | null) => amount === null ? '' : formatCurrency(amount);

    const years = sales?.years ?? [];
    const shown: DealerSalesYear | undefined = years.find(x => x.year === chosenYear) ?? years[0];
    const summary = sales?.summary;
    const change = summary && summary.sameDaysLastYear > 0
        ? (summary.yearToDate / summary.sameDaysLastYear - 1) * 100
        : undefined;

    return <React.Fragment>
        <DataGroupHeader title="Sales" />
        {
            sales && summary ?
                <div className="dealer-sales-tiles">
                    <div className="dealer-sales-tile">
                        <div className="dealer-sales-tile-label">Year to date</div>
                        <div className="dealer-sales-tile-value">{formatCurrency(summary.yearToDate)}</div>
                        <div className="dealer-sales-tile-note">
                            {
                                change === undefined
                                    ? 'No sales in the same days last year'
                                    : <span className={change >= 0 ? 'dealer-sales-up' : 'dealer-sales-down'}>
                                        <i className={`la ${change >= 0 ? 'la-arrow-up' : 'la-arrow-down'}`} aria-hidden="true" />
                                        {` ${Math.abs(change).toFixed(1)}% ${change >= 0 ? 'up' : 'down'}`}
                                    </span>
                            }
                            {change === undefined ? '' : ' on the same days last year'}
                        </div>
                    </div>
                    <div className="dealer-sales-tile">
                        <div className="dealer-sales-tile-label">Last 12 months</div>
                        <div className="dealer-sales-tile-value">{formatCurrency(summary.last12Months)}</div>
                        <div className="dealer-sales-tile-note">Up to today</div>
                    </div>
                    <OrderingTile title="Complete doors" ordering={sales.ordering.completeDoor} />
                    <OrderingTile title="Rolling steel" ordering={sales.ordering.rollingSteel} />
                </div>
                : null
        }

        {
            shown ?
                <div className="dealer-sales-panel">
                    <div className="dealer-sales-panel-heading">
                        <div className="dealer-sales-legend">
                            {
                                shown.divisions.map(d => (
                                    <span key={d.division}>
                                        <span className="dealer-sales-swatch" style={{ background: DIVISION_COLORS[d.division] }} />
                                        {DIVISION_NAMES[d.division] ?? d.division}
                                    </span>
                                ))
                            }
                        </div>
                        <label>
                            Year&nbsp;
                            <select
                                value={shown.year}
                                onChange={e => setChosenYear(parseInt(e.target.value, 10))}>
                                {years.map(x => <option key={x.year} value={x.year}>{x.year}</option>)}
                            </select>
                        </label>
                    </div>
                    {
                        shown.divisions.length > 0
                            ? <DealerSalesChart year={shown} />
                            : <p className="dealer-sales-note">There are no sales to show for {shown.year}.</p>
                    }
                </div>
                : null
        }

        {
            years.length > 0 ?
                <details className="dealer-sales-details">
                    <summary>Monthly figures</summary>
                    <div className="dealer-sales-table-scroll">
                    <Table
                        tableClass="dealer-sales-table"
                        transparentBackground={true}
                        noResultsMessage="There are no sales to show for this dealer."
                        headerClasses={['', ...MONTHS.map(() => 'table-cell-right'), 'table-cell-right']}
                        header={['Year', ...MONTHS, 'Total']}>
                        {
                            years.map((year: DealerSalesYear) => (
                                <tr key={year.year} className="chi-styled-row">
                                    <td><strong>{year.year}</strong></td>
                                    {
                                        MONTHS.map((month, idx) => (
                                            <td key={month} className="table-cell-right">{money(year.months[idx] ?? null)}</td>
                                        ))
                                    }
                                    <td className="table-cell-right"><strong>{money(year.total)}</strong></td>
                                </tr>
                            ))
                        }
                    </Table>
                    </div>
                </details>
                : null
        }
    </React.Fragment>;

};
